/**
 * Authentication hooks.
 *
 * The store holds the tokens; these hooks are the only things that create or
 * destroy a session.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { get, patch, post } from '@/lib/api'
import { queryKeys, queryScopes } from '@/lib/queryKeys'
import { useAuthStore } from '@/stores/authStore'
import { useLanguageStore } from '@/stores/languageStore'
import type { AuthResponse, CurrentUser, Language } from '@/types/api'

export interface RegistrationPayload {
  email: string
  full_name: string
  phone?: string
  preferred_language: Language
}

/** The uid and token of a set-password link, read from its URL fragment. */
export interface SetPasswordPayload {
  uid: string
  token: string
  new_password: string
}

export const useCurrentUser = () => {
  const access = useAuthStore((state) => state.access)
  const setUser = useAuthStore((state) => state.setUser)

  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: async () => {
      const user = await get<CurrentUser>('/auth/me/')
      // Keep the persisted copy in step, so a reload renders the right role
      // before this query resolves.
      setUser(user)
      return user
    },
    enabled: Boolean(access),
    staleTime: 60_000,
  })
}

export const useLogin = () => {
  const setSession = useAuthStore((state) => state.setSession)
  const setLanguage = useLanguageStore((state) => state.setLanguage)
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      post<AuthResponse>('/auth/login/', { email, password }),
    onSuccess: (data) => {
      setSession(data)
      // Honour the language the member chose when they registered.
      if (data.user.preferred_language) setLanguage(data.user.preferred_language)
      void client.invalidateQueries()
    },
  })
}

/**
 * Sign-up starts no session: the account works once the member has chosen a
 * password from the e-mailed link (`useSetPassword`). The reply is the same
 * whether or not the address already has an account.
 */
export const useRegister = () =>
  useMutation({
    mutationFn: (payload: RegistrationPayload) => post<{ detail: string }>('/auth/register/', payload),
  })

/** "Forgot password": always answers the same, whoever the address belongs to. */
export const useRequestPasswordLink = () =>
  useMutation({
    mutationFn: (email: string) => post<{ detail: string }>('/auth/password-reset/', { email }),
  })

/**
 * Choose a password from an e-mailed link -- a sign-up, a reset or a staff
 * invite. The server answers like a sign-in, so the member is signed in here.
 */
export const useSetPassword = () => {
  const setSession = useAuthStore((state) => state.setSession)
  const setLanguage = useLanguageStore((state) => state.setLanguage)
  const client = useQueryClient()

  return useMutation({
    mutationFn: (payload: SetPasswordPayload) =>
      post<AuthResponse & { detail: string }>('/auth/set-password/', payload),
    onSuccess: ({ access, refresh, user }) => {
      setSession({ access, refresh, user })
      if (user.preferred_language) setLanguage(user.preferred_language)
      void client.invalidateQueries()
    },
  })
}

export const useLogout = () => {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { refresh } = useAuthStore.getState()
      if (refresh) {
        // Best effort: blacklist the refresh token server-side. A failure here
        // must not prevent the local session from being cleared.
        try {
          await post('/auth/logout/', { refresh })
        } catch {
          /* already invalid, expired, or offline */
        }
      }
    },
    onSettled: () => {
      useAuthStore.getState().clear()
      client.clear()
    },
  })
}

export const useUpdateProfile = () => {
  const setUser = useAuthStore((state) => state.setUser)
  const setLanguage = useLanguageStore((state) => state.setLanguage)
  const client = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      full_name?: string
      phone?: string
      preferred_language?: Language
    }) => patch<CurrentUser>('/auth/me/', payload),
    onSuccess: (user) => {
      setUser(user)
      if (user.preferred_language) setLanguage(user.preferred_language)
      void client.invalidateQueries({ queryKey: queryScopes.me })
    },
  })
}

export const useChangePassword = () =>
  useMutation({
    mutationFn: (payload: { current_password: string; new_password: string }) =>
      post<{ detail: string; access: string; refresh: string }>('/auth/password/', payload),
    onSuccess: ({ access, refresh }) => {
      // The server revoked every refresh token the account had, this device's
      // included -- keep the replacement pair or the next refresh signs us out.
      useAuthStore.setState({ access, refresh })
    },
  })

/** Convenience selectors used across the UI. */
export const useSession = () => {
  const user = useAuthStore((state) => state.user)
  const access = useAuthStore((state) => state.access)
  return {
    user,
    isAuthenticated: Boolean(access),
    isStaff: Boolean(user?.is_staff),
  }
}
